import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Avatar,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  useDeleteUserMutation,
  useGetUserAdminOnyQuery,
  useGetUserInfoOnyQuery,
  useGetUserRoleQuery,
  useGetTeamQuery,
  useGetallLocationQuery,
} from "../../../Api/attoDeskApi";
import {
  IUserAdmin,
  IUserRole,
  ITeam,
  ITeam2,
  IStudentLocation,
} from "../../../Api/Interface/api.interface";
import NewUpdatePrivi from "../Edit/index";
import ChangePassword from "../ChangePassword/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import Pagination from "../../../Components/Pagination";
import PasswordIcon from "@mui/icons-material/Password";
import { capitalizeFirstLetter } from "../../../Components/Validation/capitalizeFirstLetter";
import useIsMobile from "../../../Core/hook/useIsMobile";
import GroupIcon from "@mui/icons-material/Group";
import { toProperCase } from "../../../Components/Validation/toProperCase";

// The permanent protected system account (must always be Super Admin).
// Username and email are separate; identify it by either.
export const PROTECTED_SYSTEM_USERNAME = "vmadmin";
export const PROTECTED_SYSTEM_EMAIL = "vmadmin@gmail.com";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [locationFilter, setLocationFilter] = useState<number | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const isMobileRes = useIsMobile();

  const [selectedCoursing, setSelectedCoursing] = useState<IUserAdmin | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState(false);

  const [selectedChangePassword, setSelectedChangePassword] =
    useState<IUserAdmin | null>(null);
  const [openDialogChangePassword, setOpenDialogChangePassword] =
    useState(false);

  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IUserAdmin | null>(
    null
  );

  const { data, isLoading, isError, refetch } = useGetUserAdminOnyQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetUserRoleQuery({
      searchText: "",
    });
  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const { data: locationData, isLoading: locationIsLoading } =
    useGetallLocationQuery({ searchText: "" });

  const { data: staffData, isLoading: staffIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const userroleList = useMemo(() => {
    return userroleData?.data as IUserRole[];
  }, [userroleData?.data]);

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const locationList = useMemo(() => {
    return (locationData?.data as IStudentLocation[]) || [];
  }, [locationData?.data]);

  const staffList = useMemo(() => {
    return staffData?.data as ITeam2[];
  }, [staffData?.data]);

  const userroleMap = useMemo(() => {
    const map = new Map();
    userroleList?.forEach((category) => {
      map.set(category.id, category.roleName);
    });
    return map;
  }, [userroleList]);

  // Roles that hold the System_Admin permission are superadmins and cannot be deleted.
  const superAdminRoleIds = useMemo(() => {
    const set = new Set<string>();
    userroleList?.forEach((role) => {
      if (role.allPrivileges?.includes("System_Admin")) {
        set.add(String(role.id));
      }
    });
    return set;
  }, [userroleList]);

  // A row is the protected Super Admin if it is the permanent system account
  // or holds a Super Admin (System_Admin) role. Such rows cannot be deleted
  // or disabled and show the SUPER ADMIN badge.
  const isProtectedRow = (row: IUserAdmin) =>
    (row.username || "").toLowerCase() === PROTECTED_SYSTEM_USERNAME ||
    (row.email || "").toLowerCase() === PROTECTED_SYSTEM_EMAIL ||
    superAdminRoleIds.has(String(row.user_role));

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((user) => {
      map.set(user.id, `${user.user_role_name} - ${user.username}`);
    });
    return map;
  }, [userList]);

  const locationMap = useMemo(() => {
    const map = new Map<number, string>();
    locationList?.forEach((loc) => {
      map.set(Number(loc.id), loc.location_name);
    });
    return map;
  }, [locationList]);

  const staffMap = useMemo(() => {
    const map = new Map();
    staffList?.forEach((staff) => {
      const fullName = `${capitalizeFirstLetter(
        staff.teamFirstName
      )} ${capitalizeFirstLetter(staff.teamLastName)}`;
      map.set(staff.id, fullName);
    });
    return map;
  }, [staffList]);

  const customerMap = useMemo(() => {
    const map = new Map<
      string,
      { fullName: string; email: string; phoneNumber: string; img: string }
    >();

    staffList?.forEach((customer) => {
      map.set(customer.id, {
        fullName:
          toProperCase(customer.teamFirstName || "") +
          " " +
          toProperCase(customer.teamLastName || ""),
        email: customer.teamEmail || "",
        phoneNumber: customer.teamMobileNum || "",
        img: customer.teamImg || "",
      });
    });

    return map;
  }, [staffList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDialog = (coursing: IUserAdmin) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDialogChangePassword = (coursing: IUserAdmin) => {
    setSelectedChangePassword(coursing);
    setOpenDialogChangePassword(true);
  };

  const handleCloseDialogChangePassword = () => {
    setSelectedChangePassword(null);
    setOpenDialogChangePassword(false);
  };

  const handleOpenDeletePopup = (coursing: IUserAdmin) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteUserMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the user");
    }
  };

  const allCoursings: IUserAdmin[] = Array.isArray(data?.data)
    ? (data?.data as IUserAdmin[])
    : [];

  // Client-side Campus Location filter.
  const coursings: IUserAdmin[] = locationFilter
    ? allCoursings.filter((u) => Number(u.location_id) === locationFilter)
    : allCoursings;

  const handleAddProductClick = () => {
    navigate("/user/create");
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        {/* <Grid item lg={4} md={4} sm={4} xs={4} pt={2} alignContent={"center"}>
          <Button
            variant="contained"
            className={classes.addNewButton}
            onClick={handleAddProductClick}
          >
            Add New
          </Button>
        </Grid> */}
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{ display: "flex" }}
          >
            <Grid item>
              <Autocomplete
                options={locationList}
                loading={locationIsLoading}
                getOptionLabel={(option: IStudentLocation) =>
                  option.location_name || ""
                }
                value={
                  locationList.find(
                    (loc) => Number(loc.id) === locationFilter
                  ) || null
                }
                onChange={(_, newValue: IStudentLocation | null) => {
                  setLocationFilter(newValue ? Number(newValue.id) : null);
                  setPage(0);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Campus Locations"
                    size="small"
                  />
                )}
                sx={{ width: 220 }}
              />
            </Grid>
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: {
                    root: classes.formControlList,
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="User Name  .."
                className={classes.searchTextField}
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                // variant="contained"
                className={classes.addNewButton}
                onClick={handleAddProductClick}
              >
                Add New
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}>
                  <strong>User Name</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  // sx={{ textAlign: "center" }}
                >
                  <strong>User Role</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  // sx={{ textAlign: "center" }}
                >
                  <strong>Campus Location</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  // sx={{ textAlign: "center" }}
                >
                  <strong>Create By</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  sx={{ textAlign: "right" }}
                >
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || userroleIsLoading || userIsLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || coursings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                (coursings || [])
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: IUserAdmin, index) => {
                    const customer = customerMap.get(row.staffId);
                    return (
                      <TableRow key={row.id} className={classes.tableRowHover}>
                        <TableCell
                          component="th"
                          scope="row"
                          className={classes.tablCell}
                        >
                          <Grid container spacing={1}>
                            {!isMobileRes && (
                              <Grid item>
                                {customer?.img ? (
                                  <Avatar
                                    alt="Img"
                                    src={customer.img}
                                    className={classes.avatatImage}
                                    sx={{
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  <Avatar className={classes.avatatImage}>
                                    {row?.username ? (
                                      `${row?.username ?? ""}`
                                        .trim()
                                        .charAt(0)
                                        .toUpperCase()
                                    ) : (
                                      <GroupIcon />
                                    )}
                                  </Avatar>
                                )}
                              </Grid>
                            )}
                            <Grid item textAlign={"left"} alignContent={"center"}>
                              <Typography className={classes.nameTableList}>
                                {row.username ? `${row.username}` : ""}
                              </Typography>
                              <Link
                                to={`/team-history/${row.staffId}`}
                                className={classes.iconLinkPropNormal}
                              >
                                {customer && (
                                  <Typography
                                    className={classes.mobileListText}
                                  >
                                    {customer ? `${customer.fullName}` : ""}
                                  </Typography>
                                )}
                              </Link>
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {isProtectedRow(row) ? (
                            <Chip
                              label="🛡 SUPER ADMIN"
                              size="small"
                              sx={{
                                bgcolor: "#FEF3C7",
                                color: "#92400E",
                                fontWeight: 700,
                                border: "1px solid #FCD34D",
                              }}
                            />
                          ) : (
                            userroleMap.get(row.user_role) || ""
                          )}
                        </TableCell>
                        <TableCell
                          scope="row"
                          className={classes.tablCell}
                          // sx={{ textAlign: "center" }}
                        >
                          {row.location_id
                            ? locationMap.get(Number(row.location_id)) || ""
                            : ""}
                        </TableCell>

                        <TableCell
                          scope="row"
                          className={classes.tablCell}
                          // sx={{ textAlign: "center" }}
                        >
                          {userMap.get(row.created_by) || ""}
                        </TableCell>
                        <TableCell className={classes.tablCell}>
                          <Grid
                            container
                            spacing={1}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.viewButtonList}
                                onClick={() =>
                                  handleOpenDialogChangePassword(row)
                                }
                              >
                                <PasswordIcon
                                  className={classes.viewListIcon}
                                />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.editButtonList}
                                onClick={() => handleOpenDialog(row)}
                              >
                                <EditSharpIcon
                                  className={classes.editListIcon}
                                />
                              </Button>
                            </Grid>
                            {!isProtectedRow(row) && (
                              <Grid item>
                                <Button
                                  // variant="contained"
                                  className={classes.deleteButtonList}
                                  onClick={() => handleOpenDeletePopup(row)}
                                >
                                  <DeleteOutlineSharpIcon
                                    className={classes.deleteListIcon}
                                  />
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer>

        {selectedChangePassword && (
          <ChangePassword
            openModel={openDialogChangePassword}
            handleCloseDialog={handleCloseDialogChangePassword}
            data={selectedChangePassword}
          />
        )}

        {selectedCoursing && (
          <NewUpdatePrivi
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(coursingToDelete.id.toString());
            }}
            title="Delete Position"
            content={`Are you sure you want to delete "${coursingToDelete.username}"?`}
          />
        )}
      </Grid>
    </Card>
  );
};

export default Index;
